import { useLayoutEffect } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { isDarkModeExcludedPath } from '#src/shared/darkMode';

import { useSessionConfiguration } from '#src/app/shared/hooks/sessionConfiguration';

/**
 * Client-side navigation doesn't reload the page, so the `.dark` class applied by the
 * bootstrap dark-mode script (config/app-index.ts) to a prior page can persist onto a
 * page reached via an in-app link that should be excluded from dark mode, since that
 * script only reacts to full page loads and OS `prefers-color-scheme` changes, never to
 * route changes. This reconciles the class on every route change instead — using
 * useLayoutEffect, not useEffect, so the class is corrected before the new route paints.
 *
 * Also runs on initial mount/hydration (useLayoutEffect fires then too), so it must gate on
 * the same combined condition as the bootstrap script's darkModeEnabled prop (render.tsx) —
 * the FCM_DARK_MODE_ENABLED kill-switch AND the dark_mode_rollout experiment variation.
 * Checking only the raw kill-switch here would re-enable dark mode for a user the rollout
 * experiment assigned to control, undoing the SSR-side gating right after hydration.
 */
export const DarkModeExclusion: React.FC<EmptyProps> = () => {
	const { pathname } = useLocation();
	const { featureConfigs, experiments } = useSessionConfiguration();
	const darkModeEnabled = featureConfigs.darkModeEnabled && experiments.darkModeRollout.variation === 'enabled';

	useLayoutEffect(() => {
		if (!darkModeEnabled) return;

		const rootElement = document.documentElement;
		const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
		const isDark = prefersDark && !isDarkModeExcludedPath(pathname);

		rootElement.classList.toggle('dark', isDark);
		rootElement.style.colorScheme = isDark ? 'dark' : 'light';
	}, [pathname, darkModeEnabled]);

	return null;
};
